import React, { useState } from 'react';
import { Button, Input } from '../shared';
import type { RegisterInput } from '../../types';
import styles from './AuthForms.module.css';

interface RegisterFormProps {
  onSubmit: (input: RegisterInput) => Promise<void>;
  onSwitchToLogin: () => void;
  error: string | null;
  isLoading: boolean;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSubmit,
  onSwitchToLogin,
  error,
  isLoading,
}) => {
  const [formData, setFormData] = useState<RegisterInput>({
    name: '',
    email: '',
    password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState('');

  const passwordsMatch = formData.password === confirmPassword;
  const canSubmit = formData.name && formData.email && formData.password && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!passwordsMatch) return;
    await onSubmit(formData);
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Create Account</h1>
      <p className={styles.subtitle}>Start generating AI-powered training programs</p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <Input
          type="text"
          label="Full Name"
          placeholder="John Doe"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          disabled={isLoading}
        />

        <Input
          type="email"
          label="Email"
          placeholder="trainer@example.com"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          required
          disabled={isLoading}
        />

        <Input
          type="password"
          label="Password"
          placeholder="••••••••"
          value={formData.password}
          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
          required
          minLength={8}
          disabled={isLoading}
        />

        <Input
          type="password"
          label="Confirm Password"
          placeholder="••••••••"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isLoading}
          error={confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined}
        />

        {error && <div className={styles.error}>{error}</div>}

        <Button
          type="submit"
          fullWidth
          isLoading={isLoading}
          disabled={!canSubmit}
        >
          Create Account
        </Button>
      </form>

      <p className={styles.switch}>
        Already have an account?{' '}
        <button
          type="button"
          className={styles.switchLink}
          onClick={onSwitchToLogin}
          disabled={isLoading}
        >
          Sign in
        </button>
      </p>
    </div>
  );
};
